import { DropdownMenu } from 'radix-ui';
import { Check, ChevronDown, KeyRound, LogOut, Monitor, Moon, ShieldCheck, Sun } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { toast } from 'sonner';
import type { PublicUser } from '@amg/protocol';
import { ApiError, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { useTheme, type ThemePreference } from '../lib/theme';
import { Link, navigate } from '../router';
import { BrandMark } from './BrandMark';
import { Avatar } from './ui/Avatar';
import { Button, IconButton, buttonVariants } from './ui/Button';
import { Modal } from './ui/Dialog';
import { Field, FormError, Input } from './ui/Field';

export const menuContent =
  'z-50 min-w-56 rounded-lg border border-line bg-raised p-1.5 shadow-md data-[state=closed]:animate-pop-out data-[state=open]:animate-pop-in origin-(--radix-dropdown-menu-content-transform-origin)';
export const menuItem =
  'flex h-9 cursor-pointer select-none items-center gap-2.5 rounded-sm px-2.5 text-compact text-ink-2 outline-none data-[highlighted]:bg-hover data-[highlighted]:text-ink data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:text-ink-3';

export function AppHeader({ children }: { children?: ReactNode }) {
  const { user, loading } = useAuth();
  return (
    <header className="border-b border-line/70">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link to="/" className="rounded-md" aria-label="AMG Support home">
          <BrandMark />
        </Link>
        <nav aria-label="Account" className="flex items-center gap-1">
          {children}
          <ThemeMenu />
          {loading ? (
            <span className="h-9 w-28" aria-hidden />
          ) : user ? (
            <UserMenu user={user} />
          ) : (
            <Link to="/signin" className={cn(buttonVariants.ghost, 'inline-flex h-9 items-center rounded-md px-3 text-label font-medium')}>
              Staff sign in
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}

const themeOptions: { value: ThemePreference; label: string; icon: ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <Sun aria-hidden /> },
  { value: 'dark', label: 'Dark', icon: <Moon aria-hidden /> },
  { value: 'system', label: 'Match system', icon: <Monitor aria-hidden /> },
];

export function ThemeMenu() {
  const [pref, setPref] = useTheme();
  const current = themeOptions.find((o) => o.value === pref) ?? themeOptions[2]!;
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <IconButton label={`Theme: ${current.label}`} size="md" tooltipSide="bottom">
          <span className="[&_svg]:size-[1.125rem]">{current.icon}</span>
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" sideOffset={6} className={cn(menuContent, 'min-w-44')}>
          <DropdownMenu.RadioGroup value={pref} onValueChange={(v) => setPref(v as ThemePreference)}>
            {themeOptions.map((o) => (
              <DropdownMenu.RadioItem key={o.value} value={o.value} className={menuItem}>
                {o.icon}
                <span className="flex-1">{o.label}</span>
                <DropdownMenu.ItemIndicator>
                  <Check aria-hidden className="!text-accent-text" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function UserMenu({ user }: { user: PublicUser }) {
  const { signOut } = useAuth();
  const [passwordOpen, setPasswordOpen] = useState(false);

  const onSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="flex h-10 cursor-pointer items-center gap-2 rounded-md pl-1 pr-2 text-label font-medium text-ink-2 transition-colors hover:bg-hover hover:text-ink"
          >
            <Avatar name={user.name} seed={user.id} size="sm" />
            <span className="hidden max-w-40 truncate sm:inline">{user.name}</span>
            <ChevronDown aria-hidden className="size-4 text-ink-3" />
            <span className="sr-only">Account menu</span>
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content align="end" sideOffset={6} className={menuContent}>
            <div className="px-2.5 pb-2 pt-1.5">
              <p className="truncate text-compact font-medium text-ink">{user.name}</p>
              <p className="truncate text-meta text-ink-3">{user.email}</p>
            </div>
            <DropdownMenu.Separator className="my-1 h-px bg-line" />
            {user.role === 'admin' && (
              <DropdownMenu.Item className={menuItem} onSelect={() => navigate('/admin')}>
                <ShieldCheck aria-hidden />
                Administration
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Item className={menuItem} onSelect={() => setPasswordOpen(true)}>
              <KeyRound aria-hidden />
              Change password
            </DropdownMenu.Item>
            <DropdownMenu.Separator className="my-1 h-px bg-line" />
            <DropdownMenu.Item className={menuItem} onSelect={() => void onSignOut()}>
              <LogOut aria-hidden />
              Sign out
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </>
  );
}

export function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Change password" description="Other devices signed in to your account will be signed out.">
      <ChangePasswordForm
        onDone={() => {
          onOpenChange(false);
          toast.success('Password changed');
        }}
      />
    </Modal>
  );
}

export function ChangePasswordForm({ onDone, submitLabel = 'Change password', requireCurrentHint }: { onDone: () => void; submitLabel?: string; requireCurrentHint?: string }) {
  const { changePassword } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ current?: string; next?: string; confirm?: string }>({});

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const problems: typeof fieldError = {};
    if (!current) problems.current = 'Enter your current password.';
    if (Array.from(next).length < 10) problems.next = 'Use at least 10 characters.';
    if (next !== confirm) problems.confirm = "The passwords don't match.";
    setFieldError(problems);
    if (Object.keys(problems).length > 0) return;
    setBusy(true);
    try {
      await changePassword(current, next);
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_password') setFieldError({ current: err.message });
      else if (err instanceof ApiError && err.code === 'weak_password') setFieldError({ next: err.message });
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      <FormError>{error}</FormError>
      <Field label="Current password" hint={requireCurrentHint} error={fieldError.current}>
        {(p) => <Input {...p} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}
      </Field>
      <Field label="New password" hint="At least 10 characters. A few unrelated words work well." error={fieldError.next}>
        {(p) => <Input {...p} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}
      </Field>
      <Field label="Confirm new password" error={fieldError.confirm}>
        {(p) => <Input {...p} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
      </Field>
      <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-1">
        {submitLabel}
      </Button>
    </form>
  );
}
